import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import qs from "qs";
import filterIcon from "src/assets/images/filterIcon.png";
import defaultProfile from "src/assets/images/default_profile_image.png";
import BoxWrapper from "src/Components/WebsiteComponents/BoxWrapper";
import { Loader } from "lucide-react";
import InfinteScrollWrapper from "src/Components/GlobalComponents/Widget/InfinteScrollWrapper";
import { IPaginated } from "src/types/paginated";
import { IqueryParams } from "src/types/GlobalInterfaces";

interface ReviewOverviewProps {
  productId?: any;
}

interface QueryParams extends IqueryParams {
  limit: number;
  page: number;
  rating: string;
}

// Helper function to map keys to star counts
const getStarsFromKey = (key: string): number => {
  const starMap: Record<string, number> = {
    five: 5,
    four: 4,
    three: 3,
    two: 2,
    one: 1,
  };
  return starMap[key] || 0;
};

const ProductReview: React.FC<ReviewOverviewProps> = ({ productId = "" }) => {
  const [reviewOverview, setReviewOverview] = useState<any>([]);
  const [productReviews, setProductReviews] = useState<any>([]);
  const [activeRating, setActiveRating] = useState<number | null>(null);
  const [queryParams, setQueryParams] = useState<QueryParams>({
    limit: 2,
    page: 1,
    rating: "",
  });
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [loader, setLoader] = useState<boolean>(false);
  const [reviewLoader, setReviewLoader] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);

  const fetchReviewOverview = async () => {
    setLoader(true);
    const { res, err } = await httpRequest<any>({
      method: "get",
      path: `/review/average-rating/${productId}`,
    });
    if (res) {
      setReviewOverview(res);
    } else {
      toast(err, { type: "error" });
    }
    setLoader(false);
  };

  const fetchProductReviews = async (q: QueryParams) => {
    setReviewLoader(true);
    const { res, err } = await httpRequest<any>({
      method: "get",
      path: `/review/all/${productId}?${qs.stringify(q)}`,
    });
    if (res) {
      setProductReviews((prev: any) => [...prev, ...res?.documents]);
      setPaginated(res?.paginated);
    } else {
      toast(err, { type: "error" });
    }
    setReviewLoader(false);
  };

  const handleQueryParams = (
    params: Partial<QueryParams>,
    isReset: boolean = true
  ) => {
    let q = { ...queryParams, ...params };
    setQueryParams(q);
    if (isReset) {
      setProductReviews([]);
      q.page = 1;
      q.limit = 2;
      setShowAll(false);
    }
    fetchProductReviews(q);
  };

  const handleFilterClick = (rating: number) => {
    if (activeRating === rating) {
      setActiveRating(null);
      handleQueryParams({ rating: "" });
    } else {
      setActiveRating(rating);
      handleQueryParams({ rating: rating.toString() });
    }
  };

  useEffect(() => {
    fetchReviewOverview();
    fetchProductReviews(queryParams);
  }, [productId]);

  const ratingBreakdown = reviewOverview.ratingBreakdown || {};
  const totalReviews = Object.values(ratingBreakdown).reduce(
    (acc: number, val: any) => acc + (val || 0),
    0
  );

  const handleSeeAll = () => {
    setProductReviews([]);
    if (showAll) {
      setShowAll(false);
      handleQueryParams({
        limit: 2,
      });
      const productReviewBoxWrapper = document.querySelector(".product-review-box-wrapper");
      if (productReviewBoxWrapper) {
        productReviewBoxWrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      setShowAll(true);
      handleQueryParams({
        limit: 10,
      }, false);
    }
  };

  if (loader) return <></>;

  return (
    <BoxWrapper
      title="Reviews"
      extraTextStyle={{
        fontFamily: "Lexend-Medium",
        color: "#3E3E3E",
        fontSize: "25px",
      }}
      productDetailBoxDesign
      className="product-review-box-wrapper"
    >
      {loader && (
        <div className="flex w-full min-h-[10vh] justify-center items-center">
          <Loader className="animate-spin" />
        </div>
      )}
      {reviewOverview?.totalReviews > 0 ? (
        <>
          <div className="">
            <div>
              <div className="flex gap-3 items-center">
                <div className="flex gap-3 items-center w-[50%]">
                  <span className="theme-color-green font-size-30px font-Lexend-SemiBold">
                    {reviewOverview?.averageRating.toFixed(1) || "0"}
                  </span>
                  <div className="text-yellow-400 flex flex-row reviewstars">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <FontAwesomeIcon
                        key={v}
                        className={
                          (reviewOverview?.averageRating || 0) >= v
                            ? "text-[#facc15] text-[17px] 2xl:text-[20px]"
                            : "text-[#b7b6b1] text-[17px] 2xl:text-[20px]"
                        }
                        icon={faStar}
                      />
                    ))}
                  </div>
                </div>
                {/* <div className="flex flex-row gap-1 items-center w-[50%]">
                  <span className="theme-color-green font-size-30px font-Lexend-SemiBold">
                    {reviewOverview?.recommendPercentage}%
                  </span>
                  <span className=" font-size-20px font-Poppins-Medium">
                    would recommend this product
                  </span>
                </div> */}
              </div>
              <div className="py-4 flex flex-col gap-1 lg:gap-2">
                {Object.entries(ratingBreakdown)
                  .reverse()
                  .map(([key, value], ind) => {
                    const stars = getStarsFromKey(key);
                    const percentage =
                      totalReviews > 0
                        ? ((value as any) / totalReviews) * 100
                        : 0;
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="flex items-center gap-1 min-w-fit w-[110px] 2xl:w-[120px]">
                          {[...Array(stars)].map((item) => {
                            return (
                              <FontAwesomeIcon
                                className="text-[#facc15] font-size-17px"
                                icon={faStar}
                              />
                            );
                          })}
                        </div>
                        <div className="w-[calc(100%-120px)] ml-5 max-w-[320px] bg-gray-300 h-2 rounded-full overflow-hidden">
                          <div
                            className="theme-gradient-green-bottom h-2"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <p className="font-Lexend-Medium text-[#3E3E3E] font-size-15px">
                          {Math.round(percentage)}%
                        </p>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="border-b border-[#619B97] mt-4 flex justify-between">
              <div className="flex items-center gap-3">
                <h4 className="font-size-25px font-Lexend-Medium theme-color-green pb-2 border-b-4 border-[#00443f] w-fit">
                  Review{reviewOverview?.totalReviews > 1 ? "s" : ""} ({reviewOverview?.totalReviews})
                </h4>
                {/* <h4 className="font-size-22px font-Poppins-SemiBold theme-color-green pb-2 w-fit">
                  Questions
                </h4> */}
              </div>
              {!showAll &&  paginated?.totalItems && paginated?.totalItems > 2 ? (
                <button
                  className="text-[#619B97] underline font-Lexend-Medium text-[20px]"
                  onClick={handleSeeAll}
                  id="product-review-see-all-btn"
                >
                  See all
                </button>
              ) : null}
            </div>
            <div className="mt-4 flex flex-row gap-4 items-center">
              <p className="font-size-20px font-Lexend-SemiBold text-white px-2 py-1 bg-[#00443f] rounded-md flex items-center gap-2">
                <span>
                  <img className="w-[0.6vw]" src={filterIcon} />{" "}
                </span>{" "}
                Filter
              </p>
              {[5, 4, 3, 2, 1].reverse().map((rating) => (
                <div
                  key={rating}
                  onClick={() => handleFilterClick(rating)}
                  className={`cursor-pointer px-2 py-1 border hover:border-[#c0e7a8] border-transparent rounded flex items-center gap-1 ${
                    activeRating === rating ? "bg-[#c0e7a8] text-white" : ""
                  }`}
                  id={`product-review-filter-btn-${rating}`}
                >
                  {[...Array(rating)].map((_, index) => (
                    <FontAwesomeIcon
                      key={index}
                      icon={faStar}
                      className="text-[#facc15] font-size-17px"
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <h3 className="font-Lexend-Medium theme-color-green font-size-20px">
                {productReviews.length} Review
                {productReviews.length > 1 ? "s" : ""} out of{" "}
                {paginated?.totalItems}
              </h3>
              <hr className="border-[#619B97]" />
              <InfinteScrollWrapper
                className="!pb-0 !gap-0"
                checkMore={showAll}
                isLoading={reviewLoader || loader}
                length={productReviews.length}
                currentPage={paginated.currentPage}
                totalPages={paginated.totalPages}
                onLoadMore={(page) => {
                  if (showAll) {
                    handleQueryParams({ page }, false);
                  }
                }}
                noDataOptions={{
                  // text: "No reviews found",
                  text2: "No reviews found",
                }}
              >
                {productReviews &&
                  productReviews.map((item: any, index: any) => (
                    <>
                      <div key={index} className="reviews-views-grid">
                        <div className="flex flex-col gap-4 items-center">
                          <div className="text-center flex flex-col items-center">
                            <div className="user-name-reviews !mb-0">
                              <img
                                src={`${item?.coverPhoto}`}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement; // Assert the type
                                  if (target) {
                                    target.src = defaultProfile;
                                  }
                                }}
                                alt="profile-img"
                                className="rounded-full min-w-[4.5vw] max-w-[4.5vw] object-cover min-h-[4.5vw] max-h-[4.5vw]"
                              />
                            </div>
                            {item?.firstName || item?.lastName ? (
                              <p className="font-Lexend-Medium text-[#3E3E3E] font-size-16px">
                                {(item?.firstName || "") +
                                  " " +
                                  (item?.lastName || "")}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="text-yellow-400 flex flex-row reviewstars">
                            {[1, 2, 3, 4, 5].map((v) => (
                              <FontAwesomeIcon
                                key={v}
                                className={
                                  (item?.rating || 0) >= v
                                    ? "text-[#facc15] font-size-18px"
                                    : "text-[#b7b6b1] font-size-18px"
                                }
                                icon={faStar}
                              />
                            ))}
                          </div>
                          <div>
                            {/* <span className='font-Poppins-Medium text-[#3E3E3E] font-size-18px'>{item?.message.slice(0, 30)}</span> */}
                            <p className="font-Lexend-Regular break-all text-[#3E3E3E] font-size-17px">
                              {item?.message}
                            </p>
                          </div>
                        </div>
                      </div>
                      {index !== productReviews.length - 1 && (
                        <hr className="border-[#619B97]" />
                      )}
                    </>
                  ))}
              </InfinteScrollWrapper>
              {
                showAll && reviewOverview?.totalReviews > 2 &&  !reviewLoader && !loader &&(
                <div className="flex justify-center mt-4">
                  <button
                    className="bg-[#619B97] text-white font-Lexend-Regular font-size-18px px-4 py-2 rounded-md"
                    onClick={handleSeeAll}
                    id="product-review-see-less-btn"
                  >
                    See less
                  </button>
                </div>
              )
              }
            </div>
          </div>
        </>
      ) : (
        <p className=" font-Poppins-SemiBold text-[#3E3E3E] font-size-22px text-center p-4 pt-0">
          No reviews found
        </p>
      )}
    </BoxWrapper>
  );
};

export default ProductReview;
